import {Injectable} from '@angular/core';

import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {Store} from '@stores/abstract-store';

export interface AutoscoringDraft {
  type: 'autoscoring';
  aiQF?: PL2.QuickFeedback; // AI score
  aiCmt?: string; // AI scoring rationale
  qF?: PL2.QuickFeedback; // score the teacher picked over the AI score
}

export type AiTaskDraft = AutoscoringDraft;

export interface AiTask {
  trackingIds: string[];
  /**
   * tracking id → target PK: the entry each event of the task works on (for autoscoring,
   * the student's copy of the question). Several runs can share one task, so drafts are
   * keyed by it and runs are merged or cleared per target.
   */
  targetPKs?: Record<string, string>;
  draft?: Record<string, AiTaskDraft>; // target PK → draft
}

export type AiTasksState = Record<string, AiTask>;

@Injectable({providedIn: 'root'})
export class AiTasksStore extends Store<AiTasksState> {
  constructor() {
    super({});
  }

  /** Replaces the runs and drafts of the targets it targets again, keeping the others. */
  mergeTask(
    primaryPath: string,
    trackingIds: string[],
    targetPKs: Record<string, string>,
  ) {
    const task = this.state()[primaryPath];
    if (!task) {
      this.storeTask(primaryPath, trackingIds, targetPKs);
      return;
    }

    const kept = this._withoutTargets(task, Object.values(targetPKs));
    this.setState({
      ...this.state(),
      [primaryPath]: {
        ...kept,
        trackingIds: [...kept.trackingIds, ...trackingIds],
        targetPKs: {...kept.targetPKs, ...targetPKs},
      },
    });
  }

  hasTask(primaryPath: string): boolean {
    return !!this.state()[primaryPath];
  }

  storeTask(
    primaryPath: string,
    trackingIds: string[],
    targetPKs?: Record<string, string>,
  ) {
    this.setState({
      ...this.state(),
      [primaryPath]: {trackingIds, targetPKs},
    });
  }

  retrieveTask(primaryPath: string): string[] {
    return this.state()[primaryPath]?.trackingIds ?? [];
  }

  isTracked(trackingId: string): boolean {
    return Object.values(this.state()).some((task) =>
      task.trackingIds.includes(trackingId),
    );
  }

  retrieveTargetPKs(primaryPath: string): Record<string, string> {
    return this.state()[primaryPath]?.targetPKs ?? {};
  }

  retrieveDrafts(primaryPath: string): Record<string, AiTaskDraft> {
    return this.state()[primaryPath]?.draft ?? {};
  }

  storeDraft(primaryPath: string, targetPK: string, draft: AiTaskDraft) {
    const task = this.state()[primaryPath];
    if (!task) {
      return;
    }

    this.setState({
      ...this.state(),
      [primaryPath]: {...task, draft: {...task.draft, [targetPK]: draft}},
    });
  }

  removeDraft(primaryPath: string, targetPK: string) {
    const task = this.state()[primaryPath];
    if (!task?.draft?.[targetPK]) {
      return;
    }

    const draft = {...task.draft};
    delete draft[targetPK];
    this.setState({...this.state(), [primaryPath]: {...task, draft}});
  }

  /** Drops the task too once nothing is left in it. */
  clearTargets(primaryPath: string, targetPKs: string[]) {
    const task = this.state()[primaryPath];
    if (!task) {
      return;
    }

    const kept = this._withoutTargets(task, targetPKs);
    if (U.isEmpty(kept.trackingIds) && U.isEmpty(kept.draft)) {
      this.clear(primaryPath);
      return;
    }

    this.setState({...this.state(), [primaryPath]: kept});
  }

  clear(primaryPath: string) {
    const next = {...this.state()};
    delete next[primaryPath];
    this.setState(next);
  }

  private _withoutTargets(task: AiTask, targetPKs: string[]): AiTask {
    const targets = new Set(targetPKs);
    const runs = Object.entries(task.targetPKs ?? {});
    const dropped = new Set(
      runs
        .filter(([, targetPK]) => targets.has(targetPK))
        .map(([trackingId]) => trackingId),
    );
    return {
      trackingIds: task.trackingIds.filter((id) => !dropped.has(id)),
      targetPKs: Object.fromEntries(
        runs.filter(([trackingId]) => !dropped.has(trackingId)),
      ),
      draft: Object.fromEntries(
        Object.entries(task.draft ?? {}).filter(
          ([targetPK]) => !targets.has(targetPK),
        ),
      ),
    };
  }
}
